import {Loader2} from "lucide-react";

import {useTranslation} from "react-i18next";

import {Button} from "@/components/ui/button";
import ImageCarousel from "@/components/ui/image-carousel";
import {DetailField} from "@/src/modules/core/components";
import {formatDate, formatTime} from "@/src/modules/core/lib/formatters";
import {BRIEF_READ_CTA} from "@/src/modules/briefing-boards/lib/brief-display";
import {
  tBriefingValidUntilLabel,
} from "@/src/modules/briefing-boards/lib/briefing-column-i18n";

type BriefingRecipientContentProps = {
  brief: string;
  createdBy: {name: string};
  createdAt: string;
  expiresAt?: string;
  media?: {files: string[]; thumb_files: string[]};
  showReadAction?: boolean;
  isMarkingRead?: boolean;
  onMarkRead?: () => void;
};

export function BriefingRecipientContent({
  brief,
  createdBy,
  createdAt,
  expiresAt,
  media,
  showReadAction = false,
  isMarkingRead = false,
  onMarkRead,
}: BriefingRecipientContentProps) {
  const {t: tBriefing} = useTranslation("briefing");
  const {t: tCommon} = useTranslation("common");
  const mediaFiles = media?.files ?? [];
  const mediaThumbs = media?.thumb_files ?? [];

  return (
    <>
      <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2">
        <DetailField label={tBriefing("table.sent_by")}>{createdBy.name}</DetailField>
        <DetailField label={tBriefing("table.sent_at")}>
          {formatDate(createdAt)} {formatTime(createdAt)}
        </DetailField>

        {expiresAt ? (
          <DetailField label={tBriefingValidUntilLabel(tBriefing)}>
            {formatDate(expiresAt)} {formatTime(expiresAt)}
          </DetailField>
        ) : null}

        <DetailField label={tBriefing("form.brief")} className="sm:col-span-2">
          <p className="whitespace-pre-wrap break-words">{brief}</p>
        </DetailField>

        {mediaFiles.length > 0 ? (
          <DetailField label={tCommon("images")} className="sm:col-span-2">
            <ImageCarousel images={mediaFiles} thumbnails={mediaThumbs} />
          </DetailField>
        ) : null}
      </div>

      {showReadAction && onMarkRead ? (
        <Button type="button" disabled={isMarkingRead} onClick={onMarkRead}>
          {BRIEF_READ_CTA}
          {isMarkingRead ? <Loader2 className="size-4 animate-spin" /> : null}
        </Button>
      ) : null}
    </>
  );
}
